"use client"

import { useCallback, useState } from "react"
import { Eye } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { FileIcon } from "@/components/ui/file-icon"
import { FilePreviewDialog } from "@/components/file-preview-dialog"
import type { CloudFileItem } from '@/api/cloud'
import { formatFileSize } from '@/api/cloud'

interface CloudFileGridProps {
  files: CloudFileItem[]
  selectedFiles: number[]
  onSelectChange: (ids: number[]) => void
  loading?: boolean
}

export function CloudFileGrid({ files, selectedFiles, onSelectChange, loading }: CloudFileGridProps) {
  const [previewFile, setPreviewFile] = useState<CloudFileItem | null>(null)

  const handleSelect = useCallback((id: number, checked: boolean) => {
    if (checked) {
      onSelectChange([...selectedFiles, id])
    } else {
      onSelectChange(selectedFiles.filter((fileId) => fileId !== id))
    }
  }, [selectedFiles, onSelectChange])

  // 处理卡片点击，切换选中状态
  const handleCardClick = useCallback((fileId: number) => {
    const isSelected = selectedFiles.includes(fileId)
    handleSelect(fileId, !isSelected)
  }, [selectedFiles, handleSelect])

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {Array.from({ length: 12 }).map((_, index) => (
          <Card key={index} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse h-48" />
        ))}
      </div>
    )
  }

  if (!loading && files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center bg-white border border-dashed border-gray-200 rounded-2xl">
        <p className="text-lg font-medium text-gray-900 mb-2">暂时没有文件</p>
        <p className="text-sm text-gray-500">上传或分享文件后，会自动出现在这里</p>
      </div>
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {files.map((file) => (
          <Card
            key={file.id}
            onClick={() => handleCardClick(file.id)}
            className={`group/card bg-white border rounded-xl p-3.5 hover:shadow-md transition-all cursor-pointer select-none ${
              selectedFiles.includes(file.id) ? 'border-blue-500 ring-2 ring-blue-200 shadow-sm' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="space-y-2.5">
              <div className="relative aspect-square bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center overflow-hidden group">
                <FileIcon
                  fileName={file.displayName || file.originalName}
                  fileType={file.fileType}
                  contentType={file.contentType}
                  size="xl"
                  className="transition-transform duration-200 group-hover:scale-105"
                />
                
                {/* 勾选框 */}
                <div 
                  className="absolute top-2 left-2"
                  data-checkbox
                  onClick={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    checked={selectedFiles.includes(file.id)}
                    onCheckedChange={(checked) => handleSelect(file.id, checked as boolean)}
                    className="bg-white/95 shadow-sm border-gray-300"
                  />
                </div>

                {/* 快捷预览按钮 */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setPreviewFile(file)
                  }}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/95 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity hover:text-blue-600 hover:bg-white shadow-sm border border-gray-200/80"
                  title="预览文件"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="space-y-0.5">
                <p 
                  className="text-xs font-semibold text-gray-800 truncate"
                  title={file.displayName || file.originalName}
                >
                  {file.displayName || file.originalName}
                </p>
                <div className="flex items-center justify-between text-[11px] text-gray-400">
                  <span>{formatFileSize(file.fileSize)}</span>
                  <span className="uppercase text-[10px] bg-gray-100 px-1.5 py-0.5 rounded font-mono text-gray-500">
                    {(file.displayName || file.originalName).split('.').pop() || file.fileType}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <FilePreviewDialog
        file={previewFile}
        open={!!previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </>
  )
}
